// src/pages/Dashboard.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen,
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  MapPin,
} from 'lucide-react';
import Navbar from '../components/navbar';
import TransactionalDataPreview from './Transactionaldatapreview';
import MasterDataPreview from './Masterdatapreview';
import LocationMasterPreview from './Locationmasterpreview';
import { getUserRoleFromToken, getAuthHeaders } from '../hydraApiService';

const INPUT_CLS =
  'w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 placeholder-gray-400 ' +
  'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors ' +
  'bg-white disabled:opacity-60';

const LOCATION_TYPES = ['Manufacturing', 'Distributor', 'Retailer', 'Recycler', 'Repair/Refurb'];

const LOCATION_TYPE_API_MAP = {
  'Manufacturing': 'Manufacturing',
  'Distributor':   'Distributor',
  'Retailer':      'Retailer',
  'Recycler':      'Recycler',
  'Repair/Refurb': 'Repairer',
};

const EMPTY_FORM = {
  locationId: '',
  locationType: '',
  facilityName: '',
  organizationName: '',
  addressStreet: '',
  addressCity: '',
  addressState: '',
  addressCountry: '',
  contactEmail: '',
};

function Field({ label, required, hint, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  const userRole       = getUserRoleFromToken();
  const isManufacturer = userRole === 'Manufacturer';
  const isRepairRefurb = userRole === 'Repair/Refurb';

  // ── Add Location form state ──
  const [form, setForm]               = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError]     = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const ADD_URL = 'https://dpp-api.acespireconsulting.com/api/locations/add';

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formError) setFormError(null);
    if (formSuccess) setFormSuccess(null);
  };

  const handleAddLocation = async () => {
    setFormError(null);
    setFormSuccess(null);

    const requiredMap = {
      locationId:       'Location ID',
      locationType:     'Location Type',
      facilityName:     'Facility Name',
      organizationName: 'Organization Name',
      addressCity:      'City',
      addressCountry:   'Country',
    };
    const missing = Object.entries(requiredMap)
      .filter(([key]) => !form[key]?.trim())
      .map(([, label]) => label);

    if (missing.length) {
      setFormError(`Required: ${missing.join(', ')}`);
      return;
    }

    const locationIdPattern = /^[A-Z]{2}-[A-Z]{2}-[A-Z]{2,5}-[A-Z]{2,4}-\d{3,}$/;
    if (!locationIdPattern.test(form.locationId.trim())) {
      setFormError('Location ID format invalid. Example: IN-KA-BLR-RTL-001');
      return;
    }

    if (form.contactEmail.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(form.contactEmail.trim())) {
        setFormError('Please enter a valid contact email address.');
        return;
      }
    }

    setFormLoading(true);
    try {
      const response = await axios.post(
        ADD_URL,
        {
          locationId:       form.locationId.trim(),
          locationType:     LOCATION_TYPE_API_MAP[form.locationType] || form.locationType,
          facilityName:     form.facilityName.trim(),
          organizationName: form.organizationName.trim(),
          addressStreet:    form.addressStreet.trim(),
          addressCity:      form.addressCity.trim(),
          addressState:     form.addressState.trim(),
          addressCountry:   form.addressCountry.trim(),
          contactEmail:     form.contactEmail.trim(),
        },
        { headers: getAuthHeaders() }
      );
      const action = response.data?.data?.action;
      setFormSuccess(
        action === 'updated'
          ? `Location ${form.locationId.trim()} updated successfully.`
          : `Location ${form.locationId.trim()} created successfully.`
      );
      setForm(EMPTY_FORM);
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to add location. Please try again.'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormReset = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormSuccess(null);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Top Bar */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl overflow-hidden shadow">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-700 text-sm sm:text-base"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {isRepairRefurb && (
            <button
              onClick={() => navigate('/disassembly-instructions')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Disassembly Instructions
            </button>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm sm:text-base"
          >
            Dashboard
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 sm:p-6 lg:p-8">

        {/* ── ROW 1: Master Data + Transactional Data ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <MasterDataPreview />
          <TransactionalDataPreview />
        </div>

        {/* ── ROW 2: Add Location form (Manufacturer only) ── */}
        {isManufacturer && (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-6">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100">
              <MapPin className="w-4 h-4 text-gray-700" />
              <h2 className="text-base font-semibold text-gray-800">Add Location</h2>
            </div>

            <div className="p-5">

              {formSuccess && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 mb-4">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-800 flex-1">{formSuccess}</p>
                  <button onClick={() => setFormSuccess(null)}>
                    <X className="w-4 h-4 text-green-500 hover:text-green-700" />
                  </button>
                </div>
              )}

              {formError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-700 flex-1">{formError}</p>
                  <button onClick={() => setFormError(null)}>
                    <X className="w-4 h-4 text-red-400 hover:text-red-600" />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">

                <Field label="Location ID" required hint="e.g. IN-KA-BLR-RTL-001">
                  <input
                    name="locationId"
                    value={form.locationId}
                    onChange={handleFormChange}
                    placeholder="IN-KA-BLR-RTL-001"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="Location Type" required>
                  <select
                    name="locationType"
                    value={form.locationType}
                    onChange={handleFormChange}
                    className={INPUT_CLS}
                    disabled={formLoading}
                  >
                    <option value="">Select type</option>
                    {LOCATION_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Facility Name" required>
                  <input
                    name="facilityName"
                    value={form.facilityName}
                    onChange={handleFormChange}
                    placeholder="Enter Facility Name"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="Company" required>
                  <input
                    name="organizationName"
                    value={form.organizationName}
                    onChange={handleFormChange}
                    placeholder="Enter Company Name"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="City" required>
                  <input
                    name="addressCity"
                    value={form.addressCity}
                    onChange={handleFormChange}
                    placeholder="Bangalore"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="Country" required>
                  <input
                    name="addressCountry"
                    value={form.addressCountry}
                    onChange={handleFormChange}
                    placeholder="India"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="Street Address">
                  <input
                    name="addressStreet"
                    value={form.addressStreet}
                    onChange={handleFormChange}
                    placeholder="123 Main Street"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="State">
                  <input
                    name="addressState"
                    value={form.addressState}
                    onChange={handleFormChange}
                    placeholder="Karnataka"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

                <Field label="Contact Email">
                  <input
                    type="email"
                    name="contactEmail"
                    value={form.contactEmail}
                    onChange={handleFormChange}
                    placeholder="contact@example.com"
                    className={INPUT_CLS}
                    disabled={formLoading}
                  />
                </Field>

              </div>

              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <button
                  onClick={handleAddLocation}
                  disabled={formLoading}
                  className={`flex items-center gap-2 px-6 py-2 rounded text-white text-sm font-medium transition-colors ${
                    formLoading
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                  }`}
                >
                  {formLoading
                    ? <><Loader className="w-4 h-4 animate-spin" />Saving...</>
                    : <><MapPin className="w-4 h-4" />Add Location</>
                  }
                </button>
                <button
                  onClick={handleFormReset}
                  disabled={formLoading}
                  className="px-5 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Reset
                </button>
              </div>

            </div>
          </div>
        )}

        {/* ── ROW 3: Location Master table (Manufacturer only) ── */}
        {isManufacturer && <LocationMasterPreview />}

      </div>
    </div>
  );
}

export default Dashboard;